/**
 * Conectores por nome de sistema.
 *
 * O desenho do conector de cada módulo — o que o cliente vê quando abre o pinout
 * de um sistema e quer saber qual conector é qual.
 *
 * **A imagem é o conteúdo, e ela não aparece aqui.** O arquivo mora no `~/Img` da
 * aplicação *Sistema*, não no servidor de arquivos que a API conhece, cuja raiz é
 * `C:\inetpub\diagweb.com.br\httpdocs\`. Montar uma URL a partir do nome gravado
 * seria chutar, e o resultado seria um quadro quebrado em vez de uma explicação —
 * que é exatamente o que o sistema antigo faz: quando o arquivo falta, ele serve
 * um bitmap de 1×1 e a tela mostra branco.
 *
 * **Então esta tela cuida do cadastro, e mostra o que o cadastro esconde.** São
 * três coisas que só aparecem quando alguém conta:
 *
 *  - **12 dos 278 registros não têm imagem nenhuma** — cadastro apontando para
 *    nada, que no site vira espaço em branco;
 *  - **só 6 têm a contagem conferida**, de 278. É o trabalho que falta;
 *  - **validação e ordem não são usados por registro nenhum**, e o formulário
 *    antigo nem os tem. Ficam na ficha, marcados, porque estão no contrato.
 */

import { useMemo, useState } from "react"
import {
  POSICOES, rotuloDaPosicao, usarAlterarConector, usarConectoresNomeSistema,
  usarCriarConector, usarExcluirConector, usarPublicarConector,
  type ConectorNomeSistema, type DadosDoConector,
} from "@/api/conectoresNomeSistema"
import { usarNomesDeSistema } from "@/api/catalogosDeConteudo"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import { casaBusca } from "@/kit/buscaDeTexto"
import { ordenarLista, usarOrdemDaLista } from "@/kit/ordemDaLista"
import {
  Aviso, Botao, Campo, ColunaRecolhivel, EstadoDePublicacao, FiltroDaLista, ItemDaLista, Painel, Secao,
  Vazio, estiloCampo, mensagem,
} from "./pecas"

type Recorte = "todos" | "sem-imagem" | "sem-contagem" | "fora-do-ar"

const RECORTES: { chave: Recorte; rotulo: string; ajuda: string }[] = [
  { chave: "todos", rotulo: "Todos", ajuda: "Os 278 cadastros." },
  {
    chave: "sem-imagem",
    rotulo: "Sem desenho",
    ajuda: "Cadastro que aponta para nada. No site, vira espaço em branco.",
  },
  {
    chave: "sem-contagem",
    rotulo: "Contagem não conferida",
    ajuda: "Ninguém confirmou que os pinos do desenho batem com o conector.",
  },
  { chave: "fora-do-ar", rotulo: "Fora do ar", ajuda: "Cadastrado e invisível para quem paga." },
]

const NOVO: DadosDoConector = { nomeSistemaId: 0, posicao: 1, contagem: false, validacao: false, ordem: 0 }

export function ConectoresNomeSistema() {
  const lista = usarConectoresNomeSistema({})
  const nomes = usarNomesDeSistema()

  const [recorte, setRecorte] = useState<Recorte>("todos")
  const [busca, setBusca] = useState("")
  const [ordem, setOrdem] = usarOrdemDaLista("conectores-nome-sistema")
  const [aberto, setAberto] = useState<ConectorNomeSistema | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)

  const itens = lista.data ?? []

  const contagens = useMemo(() => ({
    todos: itens.length,
    "sem-imagem": itens.filter((c) => !c.imagem).length,
    "sem-contagem": itens.filter((c) => !c.contagem).length,
    "fora-do-ar": itens.filter((c) => !c.publicado).length,
  }), [itens])

  const achados = useMemo(() => {
    const filtrados = itens.filter((c) => {
      if (recorte === "sem-imagem" && c.imagem) return false
      if (recorte === "sem-contagem" && c.contagem) return false
      if (recorte === "fora-do-ar" && c.publicado) return false
      return casaBusca(busca, c.nomeSistema, c.imagem, String(c.id))
    })
    return ordenarLista(filtrados, ordem, (c) => c.id, (c) => c.nomeSistema || `Sistema ${c.nomeSistemaId}`)
  }, [itens, recorte, busca, ordem])

  // A lista some debaixo da ficha quando o recorte muda; reabrir o mesmo id
  // mantem a ficha coerente com o que o servidor devolveu.
  const selecionado = aberto ? itens.find((c) => c.id === aberto.id) ?? null : null

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Conectores por nome de sistema"
        detalhe={`${achados.length} de ${itens.length}`}
      >
        <Botao destaque aoClicar={() => { setIncluindo(true); setAberto(null); setRecado(null) }}>
          Incluir
        </Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="Este cadastro é global: o conector de um nome de sistema vale em todos os projetos" />

      {recado && <div style={{ padding: "8px 18px 0" }}><Aviso>{recado}</Aviso></div>}

      {contagens["sem-imagem"] > 0 && recorte !== "sem-imagem" && (
        <div style={{ padding: "8px 18px 0" }}>
          <Aviso>
            <b>{contagens["sem-imagem"]} cadastros não têm desenho nenhum.</b> São linhas
            apontando para nada — no site, espaço em branco. O sistema antigo não denuncia: quando
            o arquivo falta, ele serve um bitmap de 1×1 e a tela mostra branco.
          </Aviso>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* Recorte e filtro moram no topo da coluna, como em Relés e fusíveis. */}
        <ColunaRecolhivel chave="conectores-nome-sistema" titulo="Cadastros" largura={340}
                          topo={
                            <>
                              <FiltroDaLista valor={busca} aoMudar={setBusca} rotulo="Filtrar cadastros"
                                             placeholder="Nome do sistema, arquivo ou id…"
                                             ordem={ordem} aoMudarOrdem={setOrdem} />
                              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                {RECORTES.map((r) => (
                                  <button
                                    key={r.chave}
                                    onClick={() => setRecorte(r.chave)}
                                    title={r.ajuda}
                                    aria-pressed={recorte === r.chave}
                                    style={{
                                      padding: "2px 9px", fontSize: 11, borderRadius: 12, cursor: "pointer",
                                      border: "1px solid " + (recorte === r.chave ? "var(--acao)" : "var(--line)"),
                                      background: recorte === r.chave ? "var(--surface2)" : "transparent",
                                      color: recorte === r.chave ? "var(--txt)" : "var(--txt2)",
                                    }}
                                  >
                                    {r.rotulo} ({contagens[r.chave]})
                                  </button>
                                ))}
                              </div>
                            </>
                          }>
          {lista.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
          ) : lista.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(lista.error)}</p>
          ) : achados.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Nada neste recorte.</p>
          ) : (
            achados.map((c) => (
              <ItemDaLista
                key={c.id}
                ativo={selecionado?.id === c.id}
                marcado={c.publicado}
                aoClicar={() => { setAberto(c); setIncluindo(false); setRecado(null) }}
                titulo={c.nomeSistema || `Sistema ${c.nomeSistemaId}`}
                detalhe={
                  (c.imagem ? rotuloDaPosicao(c.posicao) : "SEM DESENHO") +
                  (c.contagem ? " · conferida" : "")
                }
              />
            ))
          )}
        </ColunaRecolhivel>

        {incluindo ? (
          <Inclusao
            nomes={nomes}
            aoAvisar={setRecado}
            aoFechar={() => setIncluindo(false)}
          />
        ) : selecionado ? (
          <Ficha
            c={selecionado}
            nomes={nomes}
            aoAvisar={setRecado}
            aoFechar={() => setAberto(null)}
          />
        ) : (
          <Vazio>
            São 278 cadastros em 263 nomes de sistema — quase um por sistema. O desenho em si não
            aparece aqui: o arquivo mora no <span className="mono">~/Img</span> da aplicação
            Sistema, que não é a pasta que a API enxerga. Aqui se cuida do cadastro.
          </Vazio>
        )}
      </div>

      <BarraDeEstado direita={`${contagens["sem-contagem"]} sem contagem conferida`}>
        {`${contagens.todos} cadastros · ${contagens["sem-imagem"]} sem desenho · ${contagens["fora-do-ar"]} fora do ar`}
      </BarraDeEstado>
    </div>
  )
}

// ------------------------------------------------------------------ ficha

function Ficha({ c, nomes, aoAvisar, aoFechar }: {
  c: ConectorNomeSistema
  nomes: ReturnType<typeof usarNomesDeSistema>
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const alterar = usarAlterarConector()
  const excluir = usarExcluirConector()
  const publicar = usarPublicarConector()

  const [forma, setForma] = useState<DadosDoConector>({
    nomeSistemaId: c.nomeSistemaId, posicao: c.posicao,
    contagem: c.contagem, validacao: c.validacao, ordem: c.ordem,
  })
  const [chave, setChave] = useState(c.id)
  const [confirmando, setConfirmando] = useState(false)

  // Trocar de registro com a ficha aberta deixaria o formulário do anterior
  // sobre os dados do novo.
  if (chave !== c.id) {
    setChave(c.id)
    setForma({
      nomeSistemaId: c.nomeSistemaId, posicao: c.posicao,
      contagem: c.contagem, validacao: c.validacao, ordem: c.ordem,
    })
    setConfirmando(false)
  }

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>{c.nomeSistema || `Sistema ${c.nomeSistemaId}`}</b>
        <span className="mono" style={{ fontSize: 11, color: "var(--txt3)" }}>{c.id}</span>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      <EstadoDePublicacao
        publicado={c.publicado}
        oQue="este conector"
        ocupado={publicar.isPending}
        aoTrocar={(p) => {
          aoAvisar(null)
          publicar.mutate({ id: c.id, publicar: p }, {
            onSuccess: () => aoAvisar(p ? "Conector no ar." : "Conector fora do ar."),
            onError: (e) => aoAvisar(mensagem(e)),
          })
        }}
      />

      <Secao titulo="O desenho">
        <Desenho imagem={c.imagem} />
      </Secao>

      <Secao titulo="O cadastro">
        <Campo rotulo="Nome de sistema">
          <select value={forma.nomeSistemaId}
                  onChange={(e) => setForma({ ...forma, nomeSistemaId: Number(e.target.value) })}
                  style={{ ...estiloCampo, width: 300 }}>
            <option value={0}>escolha…</option>
            {nomes.itens.map((n) => <option key={n.id} value={n.id}>{n.rotulo}</option>)}
          </select>
        </Campo>

        <Campo rotulo="Posição" ajuda="268 dos 278 são “conector lado chicote”.">
          <select value={forma.posicao}
                  onChange={(e) => setForma({ ...forma, posicao: Number(e.target.value) })}
                  style={{ ...estiloCampo, width: 300 }}>
            {POSICOES.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
          </select>
        </Campo>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, margin: "9px 0" }}>
          <input type="checkbox" checked={forma.contagem}
                 onChange={(e) => setForma({ ...forma, contagem: e.target.checked })} />
          Contagem conferida
          <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
            — os pinos do desenho batem com o conector. Só 6 de 278 estão marcados.
          </span>
        </label>

        <NaoUsados forma={forma} aoMudar={setForma} />

        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          <Botao
            destaque
            ocupado={alterar.isPending}
            desligado={forma.nomeSistemaId === 0}
            aoClicar={() => {
              aoAvisar(null)
              alterar.mutate({ id: c.id, dados: forma }, {
                onSuccess: () => aoAvisar(`Conector ${c.id} alterado.`),
                onError: (e) => aoAvisar(mensagem(e)),
              })
            }}
          >
            Gravar
          </Botao>
          <Botao aoClicar={() => setConfirmando(true)}>Excluir</Botao>
        </div>

        {confirmando && (
          <div style={{
            marginTop: 10, padding: 10, borderRadius: 6, border: "1px solid var(--erro)",
          }}>
            <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.55 }}>
              Excluir o cadastro <b>{c.id}</b>. <b>O arquivo do desenho fica onde está</b> — nada
              aqui toca no disco do sistema antigo —, mas o conector deixa de existir para o
              cliente, em todos os projetos.
            </p>
            <div style={{ display: "flex", gap: 6 }}>
              <Botao
                ocupado={excluir.isPending}
                aoClicar={() => {
                  aoAvisar(null)
                  excluir.mutate(c.id, {
                    onSuccess: () => { aoAvisar(`Conector ${c.id} excluído.`); aoFechar() },
                    onError: (e) => { aoAvisar(mensagem(e)); setConfirmando(false) },
                  })
                }}
              >
                Confirmo
              </Botao>
              <Botao aoClicar={() => setConfirmando(false)}>Cancelar</Botao>
            </div>
          </div>
        )}
      </Secao>
    </Painel>
  )
}

/**
 * O que se sabe do desenho — que é o nome do arquivo e onde ele mora.
 *
 * **Não monto uma URL.** O nome gravado é `conectorNomeSistema{id}.{ext}` e o
 * arquivo está no `~/Img` da aplicação Sistema; a API não serve aquela pasta.
 * Um `<img>` apontando para um palpite daria quadro quebrado, que é pior do que
 * a frase — e é o que o sistema antigo entrega hoje, com o bitmap de 1×1.
 */
function Desenho({ imagem }: { imagem: string | null }) {
  if (!imagem) {
    return (
      <p style={{ margin: 0, fontSize: 11.5, color: "var(--erro)", lineHeight: 1.55 }}>
        <b>Sem desenho.</b> O cadastro existe e aponta para nada: no site, espaço em branco. Para
        resolver, o arquivo tem de ser anexado no sistema antigo — a API ainda não aceita o
        binário deste módulo.
      </p>
    )
  }

  const ehSvg = imagem.toLowerCase().endsWith(".svg")

  return (
    <>
      <p style={{ margin: "0 0 4px", fontSize: 11.5, lineHeight: 1.55 }}>
        <span className="mono">{imagem}</span>
        {ehSvg && <span style={{ color: "var(--txt3)" }}> · vetor</span>}
      </p>
      <p style={{ margin: 0, fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
        O arquivo mora no <span className="mono">~/Img</span> da aplicação Sistema, que não é a
        pasta que a API enxerga — por isso ele não aparece aqui. Para ver o desenho, abra o
        cadastro no sistema antigo.
      </p>
    </>
  )
}

/**
 * Validação e ordem.
 *
 * **Nenhum dos 278 registros usa os dois**, e o formulário do sistema antigo nem
 * os tem. Ficam aqui porque estão no contrato da API e alguém pode ter um plano
 * para eles — mas escondidos atrás de um resumo, para não parecerem campos de
 * trabalho no meio dos que são.
 */
function NaoUsados({ forma, aoMudar }: {
  forma: DadosDoConector
  aoMudar: (f: DadosDoConector) => void
}) {
  return (
    <details style={{ marginTop: 4 }}>
      <summary style={{ fontSize: 11, color: "var(--txt3)", cursor: "pointer" }}>
        Validação e ordem — nenhum registro usa
      </summary>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 7 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={forma.validacao}
                 onChange={(e) => aoMudar({ ...forma, validacao: e.target.checked })} />
          Validação
        </label>
        <Campo rotulo="Ordem">
          <input type="number" value={forma.ordem}
                 onChange={(e) => aoMudar({ ...forma, ordem: Number(e.target.value) })}
                 style={{ ...estiloCampo, width: 90 }} />
        </Campo>
      </div>
    </details>
  )
}

// ------------------------------------------------------------------ inclusão

function Inclusao({ nomes, aoAvisar, aoFechar }: {
  nomes: ReturnType<typeof usarNomesDeSistema>
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const criar = usarCriarConector()
  const [forma, setForma] = useState<DadosDoConector>(NOVO)
  const [ciente, setCiente] = useState(false)

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <b style={{ fontSize: 13.5 }}>Incluir conector</b>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      <Aviso>
        <b>O desenho não sobe por aqui.</b> A rota de cadastro da API aceita só o registro — o
        binário está pendente do serviço de arquivos. O que nasce daqui é um cadastro
        <b> sem desenho</b>, e desenho é o conteúdo: no site, espaço em branco até alguém anexar o
        arquivo pelo sistema antigo. Hoje já existem 12 registros nessa situação.
      </Aviso>

      <Secao titulo="O cadastro">
        <Campo rotulo="Nome de sistema">
          <select value={forma.nomeSistemaId}
                  onChange={(e) => setForma({ ...forma, nomeSistemaId: Number(e.target.value) })}
                  style={{ ...estiloCampo, width: 300 }}>
            <option value={0}>escolha…</option>
            {nomes.itens.map((n) => <option key={n.id} value={n.id}>{n.rotulo}</option>)}
          </select>
        </Campo>

        <Campo rotulo="Posição">
          <select value={forma.posicao}
                  onChange={(e) => setForma({ ...forma, posicao: Number(e.target.value) })}
                  style={{ ...estiloCampo, width: 300 }}>
            {POSICOES.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
          </select>
        </Campo>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, margin: "9px 0" }}>
          <input type="checkbox" checked={forma.contagem}
                 onChange={(e) => setForma({ ...forma, contagem: e.target.checked })} />
          Contagem conferida
        </label>

        <label style={{
          display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11.5,
          margin: "12px 0 8px", lineHeight: 1.5, maxWidth: "62ch",
        }}>
          <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)}
                 style={{ marginTop: 2 }} />
          Entendi que o cadastro vai nascer sem desenho, e que o arquivo precisa ser anexado pelo
          sistema antigo.
        </label>

        <div style={{ display: "flex", gap: 6 }}>
          <Botao
            destaque
            ocupado={criar.isPending}
            desligado={forma.nomeSistemaId === 0 || !ciente}
            aoClicar={() => {
              aoAvisar(null)
              criar.mutate(forma, {
                onSuccess: (r) => {
                  aoAvisar(`Conector ${r.id} criado — sem desenho. Anexe o arquivo no sistema antigo.`)
                  aoFechar()
                },
                onError: (e) => aoAvisar(mensagem(e)),
              })
            }}
          >
            Criar o cadastro
          </Botao>
          <Botao aoClicar={aoFechar}>Cancelar</Botao>
        </div>
      </Secao>
    </Painel>
  )
}
